import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Flag, Files, Eye, ChevronDown, Waypoints, Users, Target, Globe2, ThumbsUp, Calendar, MapPin, Phone, Mail, Globe, Youtube, Facebook, Twitter, ArrowRight, Award, Briefcase, Handshake } from 'lucide-react';
import './afrique.css';
import { Link } from 'react-router-dom';
import { ArticleService } from './apiService';
import { translations } from './translations';

// Lazy loading des composants lourds
const GallerySection    = lazy(() => import('./components/GallerySection'));
const SecretariatSection = lazy(() => import('./components/SecretariatSection'));

const App = ({ defaultLang = 'en' }) => {

  /* ------------------------------------------------------------------ */
  /*  État global                                                          */
  /* ------------------------------------------------------------------ */
  const [uiState, setUiState] = useState({
    isScrolled:          false,
    currentSlide:        0,
    showFullMessage:     false,
    activeAboutSlide:    0,
    activePresident:     0,
    activeDocumentSlide: 0,
    currentLanguage:     defaultLang,
  });

  const [expandedMembers,  setExpandedMembers]  = useState({});
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);
  const [articles,         setArticles]         = useState([]);
  const [loadingArticles,  setLoadingArticles]  = useState(true);
  const [articlesError,    setArticlesError]    = useState(null);

  /* ------------------------------------------------------------------ */
  /*  Traductions actives                                                  */
  /* ------------------------------------------------------------------ */
  const t = translations[uiState.currentLanguage] || translations.en;

  /* ------------------------------------------------------------------ */
  /*  Données localisées (memoïsées depuis les traductions)               */
  /* ------------------------------------------------------------------ */
  const aboutSlides = useMemo(() => t.aboutSlides.map((s, i) => ({
    ...s,
    icon: [Target, ThumbsUp, Award, Globe, Waypoints, Handshake][i] || Target,
    features: s.features.map((f, j) => ({
      ...f,
      icon: [Target, ThumbsUp, Award, Globe, Waypoints, Handshake][j] || Target,
    })),
  })), [t]);

  const kpiData = useMemo(() => t.kpi.items.map((k, i) => ({
    ...k,
    icon:     [Globe2, Eye, Files, Flag][i],
    gradient: ["linear-gradient(135deg, #c22027, #a01c22)", "linear-gradient(135deg, #dc2626, #991b1b)", "linear-gradient(135deg, #dc2626, #991b1b)", "linear-gradient(135deg, #ef4444, #dc2626)"][i],
  })), [t]);

  const steeringCommitteeData = useMemo(() => t.steeringMembersData, [t]);
  const marrakechDocumentsData = useMemo(() => t.marrakechDocuments, [t]);
  const presidentialData = useMemo(() => [t.presidentData, t.vicePresidentData], [t]);

  const teamMembers = useMemo(() => [
    { name: "Morocco",      role: "Member", image: "maroc.jpg" },
    { name: "Mozambique",   role: "Member", image: "mozanbique.jpg" },
    { name: "Benin",        role: "Member", image: "benin.jpg" },
    { name: "South Africa", role: "Member", image: "southafrica.jpg" },
    { name: "Rwanda",       role: "Member", image: "rwanda.jpg" },
    { name: "Mali",         role: "Member", image: "mali.jpg" },
    { name: "Nigeria",      role: "Member", image: "nigeria.jpg" },
    { name: "Cabo Verde",   role: "Member", image: "capvert.jpg" },
    { name: "Tunisia",      role: "Member", image: "tunisia.jpg" },
    { name: "Mauritania",   role: "Member", image: "mauritania.jpg" },
    { name: "Senegal",      role: "Member", image: "senegal.jpg" },
    { name: "Mauritius",    role: "Member", image: "mauritius.jpg" },
    { name: "Burkina Faso", role: "Member", image: "burkina faso.jpg" },
    { name: "Togo",         role: "Member", image: "togo.jpg" },
    { name: "Madagascar",   role: "Member", image: "madagascar.jpg" },
  ], []);

  /* ------------------------------------------------------------------ */
  /*  Handlers                                                             */
  /* ------------------------------------------------------------------ */
  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 50;
    setUiState(prev => prev.isScrolled !== scrolled ? { ...prev, isScrolled: scrolled } : prev);
  }, []);

  const updateUiState = useCallback((updates) => {
    setUiState(prev => {
      const next = { ...prev, ...updates };
      if (updates.currentLanguage) {
        localStorage.setItem('lang', updates.currentLanguage);
      }
      return next;
    });
  }, []);

  const toggleMemberBio = useCallback((index) => {
    setExpandedMembers(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const scrollToSection = useCallback((sectionId, offset = 80) => {
    const el = document.getElementById(sectionId);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
    }
  }, []);

  const closeMenu = useCallback(() => setMobileMenuOpen(false), []);

  /* ------------------------------------------------------------------ */
  /*  Effects                                                              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (!ticking) { requestAnimationFrame(() => { handleScroll(); ticking = false; }); ticking = true; }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [handleScroll]);

  useEffect(() => {
    const t2 = setInterval(() => {
      setUiState(prev => {
        if (aboutSlides.length === 0) return prev;
        return { ...prev, activeAboutSlide: (prev.activeAboutSlide + 1) % aboutSlides.length };
      });
    }, 60000);
    return () => clearInterval(t2);
  }, [aboutSlides.length]);

  useEffect(() => {
    const t3 = setInterval(() => {
      setUiState(prev => {
        if (marrakechDocumentsData.length === 0) return prev;
        return { ...prev, activeDocumentSlide: (prev.activeDocumentSlide + 1) % marrakechDocumentsData.length };
      });
    }, 60000);
    return () => clearInterval(t3);
  }, [marrakechDocumentsData.length]);

  // Charger les articles
  const loadLatestArticles = async () => {
    setLoadingArticles(true);
    setArticlesError(null);
    try {
      const response = await ArticleService.getLatestPublished();
      const arr = response.data?.data || response.data || [];
      setArticles(Array.isArray(arr) ? arr : []);
    } catch (err) {
      setArticlesError('Impossible de charger les articles.');
      setArticles([]);
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => { loadLatestArticles(); }, []);

  // Auto-rotation carousel articles
  useEffect(() => {
    if (articles.length === 0) return;
    const interval = setInterval(() => {
      setUiState(prev => ({ ...prev, currentSlide: (prev.currentSlide + 1) % articles.length }));
    }, 5000);
    return () => clearInterval(interval);
  }, [articles.length]);

  /* ------------------------------------------------------------------ */
  /*  Rendu                                                                */
  /* ------------------------------------------------------------------ */
  return (
    <div className="min-h-screen">

      {/* ============================================================
          HEADER
      ============================================================ */}
      <header className={`header ${uiState.isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">

            {/* Logo */}
            <div className="logo">
              <img src="logo.png" alt="ANPMN Logo" className="logo-image" loading="eager" />
            </div>

            {/* Navigation desktop */}
            <nav className="nav">
              <div className="nav-dropdown">
                <a href="#apropos" className="nav-dropdown-toggle">{t.nav.aboutUs}</a>
                <div className="nav-dropdown-menu">
                  <a href="#about-missions" onClick={(e) => { e.preventDefault(); updateUiState({ activeAboutSlide: 0 }); scrollToSection('apropos'); }}>{t.nav.ourMission}</a>
                  <a href="#about-vision"   onClick={(e) => { e.preventDefault(); updateUiState({ activeAboutSlide: 1 }); scrollToSection('apropos'); }}>{t.nav.ourVision}</a>
                </div>
              </div>

              <div className="nav-dropdown">
                <a href="#equipe" className="nav-dropdown-toggle">{t.nav.organization}</a>
                <div className="nav-dropdown-menu">
                  <a href="#equipe"                     onClick={(e) => { e.preventDefault(); scrollToSection('equipe'); }}>{t.nav.members}</a>
                  <a href="#steering-committee-section" onClick={(e) => { e.preventDefault(); scrollToSection('steering-committee-section'); }}>{t.nav.steeringCommittee}</a>
                </div>
              </div>

              <div className="nav-dropdown">
                <a href="#partners" className="nav-dropdown-toggle">{t.nav.partners}</a>
                <div className="nav-dropdown-menu">
                  <a href="https://www.un.org/en/"                             target="_blank" rel="noopener noreferrer">{t.nav.unitedNations}</a>
                  <a href="https://www.ohchr.org/en/treaty-bodies/spt"        target="_blank" rel="noopener noreferrer">{t.nav.spt}</a>
                  <a href="https://www.coe.int/en/web/cpt"                    target="_blank" rel="noopener noreferrer">{t.nav.cpt}</a>
                </div>
              </div>

              <a href="#marrakech-event-section">{t.nav.foundingDocuments}</a>
              <a href="#actualites">{t.nav.newsEvents}</a>
            </nav>

            {/* Actions */}
            <div className="header-actions">
              {/* Switch de langue */}
              <div className="language-switch">
                <button className={`lang-btn ${uiState.currentLanguage === 'en' ? 'active' : ''}`} onClick={() => updateUiState({ currentLanguage: 'en' })}>EN</button>
                <span className="lang-divider">|</span>
                <button className={`lang-btn ${uiState.currentLanguage === 'fr' ? 'active' : ''}`} onClick={() => updateUiState({ currentLanguage: 'fr' })}>FR</button>
              </div>

              {/* Bouton Secrétariat */}
              <button className="header-btn" onClick={(e) => { e.preventDefault(); scrollToSection('secretariat-section', 100); }}>
                <MapPin size={18} />
                <span>{t.nav.permanentSecretariat}</span>
              </button>

              {/* Hamburger mobile */}
              <button className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
                <span></span><span></span><span></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================
          MENU MOBILE DRAWER
      ============================================================ */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMenu} />
      <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <img src="logo.png" alt="ANPMN Logo" className="mobile-menu-logo" />
          <button className="mobile-menu-close" onClick={closeMenu} aria-label="Fermer">✕</button>
        </div>

        {/* Switch de langue dans le drawer */}
        <div className="mobile-menu-lang">
          <button className={`mobile-lang-btn ${uiState.currentLanguage === 'en' ? 'active' : ''}`} onClick={() => { updateUiState({ currentLanguage: 'en' }); closeMenu(); }}>EN</button>
          <span className="mobile-lang-sep">|</span>
          <button className={`mobile-lang-btn ${uiState.currentLanguage === 'fr' ? 'active' : ''}`} onClick={() => { updateUiState({ currentLanguage: 'fr' }); closeMenu(); }}>FR</button>
        </div>

        <nav className="mobile-menu-nav">
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">{t.nav.aboutUs}</span>
            <a className="mobile-menu-link" href="#about-missions" onClick={(e) => { e.preventDefault(); updateUiState({ activeAboutSlide: 0 }); scrollToSection('apropos'); closeMenu(); }}>{t.nav.ourMission}</a>
            <a className="mobile-menu-link" href="#about-vision"   onClick={(e) => { e.preventDefault(); updateUiState({ activeAboutSlide: 1 }); scrollToSection('apropos'); closeMenu(); }}>{t.nav.ourVision}</a>
          </div>
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">{t.nav.organization}</span>
            <a className="mobile-menu-link" href="#equipe"                     onClick={(e) => { e.preventDefault(); scrollToSection('equipe'); closeMenu(); }}>{t.nav.members}</a>
            <a className="mobile-menu-link" href="#steering-committee-section" onClick={(e) => { e.preventDefault(); scrollToSection('steering-committee-section'); closeMenu(); }}>{t.nav.steeringCommittee}</a>
          </div>
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">{t.nav.partners}</span>
            <a className="mobile-menu-link" href="https://www.un.org/en/"                      target="_blank" rel="noopener noreferrer" onClick={closeMenu}>{t.nav.unitedNations}</a>
            <a className="mobile-menu-link" href="https://www.ohchr.org/en/treaty-bodies/spt" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>SPT</a>
            <a className="mobile-menu-link" href="https://www.coe.int/en/web/cpt"              target="_blank" rel="noopener noreferrer" onClick={closeMenu}>CPT</a>
          </div>
          <a className="mobile-menu-link mobile-menu-link--standalone" href="#marrakech-event-section" onClick={(e) => { e.preventDefault(); scrollToSection('marrakech-event-section'); closeMenu(); }}>{t.nav.foundingDocuments}</a>
          <a className="mobile-menu-link mobile-menu-link--standalone" href="#actualites"              onClick={(e) => { e.preventDefault(); scrollToSection('actualites'); closeMenu(); }}>{t.nav.newsEvents}</a>
          <button className="mobile-menu-secretariat" onClick={() => { scrollToSection('secretariat-section', 100); closeMenu(); }}>
            <MapPin size={16} />
            {t.nav.permanentSecretariat}
          </button>
        </nav>
      </div>

      {/* ============================================================
          HERO
      ============================================================ */}
      <section id="accueil" className="hero-premium">
        {/* Carousel background */}
        <div className="hero-carousel-container">
          {articles.length > 0 ? (
            articles.map((article, index) => (
              <div key={article.id} className={`hero-carousel-slide ${index === uiState.currentSlide ? 'active' : ''}`}>
                <img src={article.image || '/0a.png'} alt={article.title} loading={index === 0 ? 'eager' : 'lazy'} />
                <div className="hero-carousel-overlay"></div>
              </div>
            ))
          ) : (
            <div className="hero-carousel-slide active">
              <img src="/0a.png" alt="Welcome" loading="eager" />
              <div className="hero-carousel-overlay"></div>
            </div>
          )}
        </div>

        {/* Content layer */}
        <div className="hero-premium-content">
          <div className="container hero-container">
            <div className="hero-content-grid">
              {/* Colonne gauche */}
              <div className="hero-main-content">
                <h1 className="hero-premium-title">
                  {articles.length > 0 ? articles[uiState.currentSlide]?.title : t.hero.welcome}
                </h1>
                <p className="hero-premium-description">
                  {articles.length > 0 ? articles[uiState.currentSlide]?.excerpt : t.hero.subtitle}
                </p>
                <div className="hero-meta-info">
                  <div className="hero-date">
                    <Calendar size={18} />
                    <span>
                      {articles.length > 0 && articles[uiState.currentSlide]?.publish_date
                        ? new Date(articles[uiState.currentSlide].publish_date).toLocaleDateString(uiState.currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                        : ''}
                    </span>
                  </div>
                </div>
                <Link to={articles.length > 0 ? `/news/${articles[uiState.currentSlide]?.id}` : '#'} className="hero-premium-btn">
                  {t.hero.readFullArticle}
                  <ArrowRight size={20} />
                </Link>
              </div>

              {/* Colonne droite — news sidebar */}
              <div className="hero-news-sidebar">
                <div className="hero-sidebar-header">
                  <h3>{t.hero.recentNews}</h3>
                  <div className="sidebar-divider"></div>
                </div>
                <div className="hero-news-list">
                  {articles.length > 0 ? (
                    articles.map((article, index) => (
                      <div key={article.id} className={`hero-news-card ${index === uiState.currentSlide ? 'active' : ''}`} onClick={() => setUiState(prev => ({ ...prev, currentSlide: index }))}>
                        <div className="news-card-image">
                          <img src={article.imageArticle || article.image || '/9.jpg'} alt={article.title} loading="lazy" />
                          <div className="news-card-overlay"></div>
                        </div>
                        <div className="news-card-content">
                          <div className="news-card-date">
                            <Calendar size={14} />
                            <span>{new Date(article.publish_date).toLocaleDateString(uiState.currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          </div>
                          <h4 className="news-card-title">{article.title}</h4>
                          <div className="news-card-arrow"><ArrowRight size={18} /></div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#999', textAlign: 'center' }}>{t.hero.loading}</p>
                  )}
                </div>
                <Link to="/news" className="view-all-news-btn">
                  {t.hero.viewAllNews}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Indicators */}
        <div className="hero-premium-indicators">
          {(articles.length > 0 ? articles : [1, 2, 3]).map((_, index) => (
            <button key={index} onClick={() => setUiState(prev => ({ ...prev, currentSlide: index }))} className={`hero-indicator ${index === uiState.currentSlide ? 'active' : ''}`} aria-label={`Slide ${index + 1}`}>
              <span className="indicator-progress"></span>
            </button>
          ))}
        </div>

        {/* Navigation arrows */}
        <button className="hero-nav-arrow hero-nav-prev" onClick={() => setUiState(prev => ({ ...prev, currentSlide: (prev.currentSlide - 1 + (articles.length || 3)) % (articles.length || 3) }))} aria-label="Previous">
          <ChevronDown style={{ transform: 'rotate(90deg)' }} size={32} />
        </button>
        <button className="hero-nav-arrow hero-nav-next" onClick={() => setUiState(prev => ({ ...prev, currentSlide: (prev.currentSlide + 1) % (articles.length || 3) }))} aria-label="Next">
          <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={32} />
        </button>

        <div className="hero-scroll-indicator">
          <div className="scroll-mouse"><div className="scroll-wheel"></div></div>
          <span>{t.hero.scrollToExplore}</span>
        </div>
      </section>

      {/* ============================================================
          LEADERSHIP
      ============================================================ */}
      <section className="section">
        <div className="container">
          <div className="section-title">
            <h2>{t.leadership.sectionTitle}</h2>
            <div className="divider"></div>
            <div className="president-navigation">
              <div className="president-nav-content">
                <div className="president-indicators">
                  <button onClick={() => updateUiState({ activePresident: 0 })} className={`president-indicator ${uiState.activePresident === 0 ? 'active' : ''}`}><span>{t.leadership.chair}</span></button>
                  <button onClick={() => updateUiState({ activePresident: 1 })} className={`president-indicator ${uiState.activePresident === 1 ? 'active' : ''}`}><span>{t.leadership.viceChair}</span></button>
                </div>
              </div>
            </div>
          </div>

          <div className="president-section">
            <div className="president-card">
              <div className="president-image-container">
                <div className="president-image">
                  <img src={presidentialData[uiState.activePresident].image} alt={presidentialData[uiState.activePresident].name} loading="lazy" />
                </div>
                <div className="president-quote"><p>{presidentialData[uiState.activePresident].quote}</p></div>
              </div>

              <div className="president-content">
                <div className="president-header">
                  <h3 className="president-name">{presidentialData[uiState.activePresident].name}</h3>
                  <p className="president-title">{presidentialData[uiState.activePresident].title}</p>
                  <p className="president-credentials">{presidentialData[uiState.activePresident].credentials}</p>
                </div>

                <div className="president-achievements">
                  <h4>{t.leadership.distinctions}</h4>
                  <div className="achievements-grid">
                    {presidentialData[uiState.activePresident].achievements.map((a, i) => (
                      <div key={i} className="achievement-item">
                        <div className="achievement-icon"><Award size={16} /></div>
                        <span>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="president-message">
                  <h4>{uiState.activePresident === 0 ? t.leadership.wordByChair : t.leadership.wordByViceChair}</h4>
                  <div className="message-content">
                    <p>{uiState.showFullMessage ? presidentialData[uiState.activePresident].fullMessage : presidentialData[uiState.activePresident].shortMessage}</p>
                    <button className="read-more-btn" onClick={() => updateUiState({ showFullMessage: !uiState.showFullMessage })}>
                      {uiState.showFullMessage ? (<>{t.leadership.readLess}<ChevronDown size={16} style={{ transform: 'rotate(180deg)', transition: 'transform 0.3s ease' }} /></>) : (<>{t.leadership.readMore}<ArrowRight size={16} /></>)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ABOUT (MISSION / VISION)
      ============================================================ */}
      <section id="apropos" className="about-revolutionary" style={{ backgroundImage: `url(${aboutSlides[uiState.activeAboutSlide].bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="about-slide-overlay"></div>
        <div className="container about-slide-content">
          <div className="about-content-grid">
            <div className="about-main-content">
              <div className="about-header">
                <span className="about-subtitle">{aboutSlides[uiState.activeAboutSlide].subtitle}</span>
                <h2 className="about-title">{aboutSlides[uiState.activeAboutSlide].title}</h2>
                <div className="about-title-underline"></div>
                <p className="about-description">{aboutSlides[uiState.activeAboutSlide].description}</p>
              </div>
              <div className="about-features-modern">
                {aboutSlides[uiState.activeAboutSlide].features.map((feature, idx) => (
                  <div key={idx} className="feature-modern">
                    <div className="feature-modern-icon"><feature.icon size={28} /></div>
                    <div className="feature-modern-content">
                      <h2>{feature.title}</h2>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="about-image-modern">
              <div className="about-image-wrapper">
                <img src={aboutSlides[uiState.activeAboutSlide].image} alt={aboutSlides[uiState.activeAboutSlide].title} loading="lazy" />
                <div className="about-image-decoration"></div>
              </div>
              <div className="floating-particles">
                {[...Array(6)].map((_, i) => (<div key={i} className={`particle particle-${i + 1}`} style={{ animationDelay: `${i * 0.5}s` }}></div>))}
              </div>
              <div className="about-navigation">
                <div className="about-nav-content">
                  <button className="about-nav-btn prev" onClick={() => updateUiState({ activeAboutSlide: (uiState.activeAboutSlide - 1 + aboutSlides.length) % aboutSlides.length })}>
                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={24} />
                  </button>
                  <div className="about-indicators">
                    {aboutSlides.map((_, index) => (
                      <button key={index} onClick={() => updateUiState({ activeAboutSlide: index })} className={`about-indicator ${index === uiState.activeAboutSlide ? 'active' : ''}`}>
                        <span>{aboutSlides[index].title}</span>
                      </button>
                    ))}
                  </div>
                  <button className="about-nav-btn next" onClick={() => updateUiState({ activeAboutSlide: (uiState.activeAboutSlide + 1) % aboutSlides.length })}>
                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="about-progress">
          <div className="about-progress-bar" style={{ width: `${((uiState.activeAboutSlide + 1) / aboutSlides.length) * 100}%` }}></div>
        </div>
      </section>

      {/* ============================================================
          KPI
      ============================================================ */}
      <section className="kpi-statistics-section">
        <div className="container">
          <div className="section-title">
            <h2>{t.kpi.sectionTitle}</h2>
            <div className="divider"></div>
          </div>
          <div className="kpi-grid">
            {kpiData.map((kpi, index) => (
              <div key={index} className="kpi-card" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="kpi-background" style={{ background: kpi.gradient }}></div>
                <div className="kpi-content">
                  <div className="kpi-icon-container">
                    <div className="kpi-icon"><kpi.icon size={32} /></div>
                    <div className="kpi-pulse"></div>
                  </div>
                  <div className="kpi-number">{kpi.number}</div>
                  <div className="kpi-label">{kpi.label}</div>
                  <div className="kpi-description">{kpi.description}</div>
                </div>
                <div className="kpi-shine"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          MEMBRES
      ============================================================ */}
      <section id="equipe" className="section section-gray">
        <div className="container">
          <div className="section-title">
            <h2>{t.members.sectionTitle}</h2>
            <div className="divider"></div>
            <p>{t.members.description}</p>
          </div>
          <div className="grid grid-4">
            {teamMembers.map((member) => (
              <div key={member.name} className="team-card">
                <div className="team-image"><img src={member.image} alt={member.name} loading="lazy" /></div>
                <div className="team-info"><h3 className="team-name">{member.name}</h3></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          STEERING COMMITTEE
      ============================================================ */}
      <section id="steering-committee-section" className="steering-committee-section">
        <div className="container">
          <div className="section-title">
            <h2>{t.steeringCommittee.sectionTitle}</h2>
            <div className="divider"></div>
          </div>
          <div className="steering-grid">
            {steeringCommitteeData.map((member, index) => (
              <div key={index} className="steering-member-card">
                <div className="member-header">
                  <div className="member-image-container">
                    <img src={member.image} alt={member.name} className="member-image" loading="lazy" />
                    <div className="member-flag"><img src={member.flag} alt={member.country} loading="lazy" /></div>
                  </div>
                  <div className="member-country-badge">{member.country}</div>
                </div>
                <div className="member-info">
                  <h3 className="member-name">{member.name}</h3>
                  <div className="member-role">{member.role}</div>
                </div>
                {member.hasBiography && (
                  <div className="member-bio">
                    <h4>{t.steeringCommittee.biography}</h4>
                    <div className="bio-content">
                      <p>{expandedMembers[index] ? member.fullBio : member.shortBio}</p>
                      <button className="bio-toggle-btn" onClick={() => toggleMemberBio(index)}>
                        {expandedMembers[index]
                          ? (<>{t.steeringCommittee.readLess}<ChevronDown size={16} style={{ transform: 'rotate(180deg)', transition: 'transform 0.3s ease' }} /></>)
                          : (<>{t.steeringCommittee.readMore}<ArrowRight size={16} /></>)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          DOCUMENTS FONDATEURS (MARRAKECH)
      ============================================================ */}
      <section id="marrakech-event-section" className="marrakech-revolutionary" style={{ backgroundImage: `url(0c.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="marrakech-slide-overlay"></div>

        <div className="marrakech-section-header">
          <div className="container">
            <span className="marrakech-date-badge"><Calendar size={18} />{marrakechDocumentsData[uiState.activeDocumentSlide].date}</span>
            <h2 className="marrakech-main-title">{marrakechDocumentsData[uiState.activeDocumentSlide].title}</h2>
            <div className="marrakech-main-underline"></div>
            <div className="marrakech-location-badge"><MapPin size={18} />{marrakechDocumentsData[uiState.activeDocumentSlide].location}</div>
          </div>
        </div>

        <div className="container marrakech-slide-content">
          <div className="marrakech-content-grid">
            <div className="marrakech-main-content">
              <div className="marrakech-description-card">
                <p className="marrakech-description">{marrakechDocumentsData[uiState.activeDocumentSlide].description}</p>
              </div>
              <div className="marrakech-highlights-section">
                <h3 className="highlights-title">{t.marrakech.keyHighlights}</h3>
                <div className="marrakech-highlights-grid">
                  {marrakechDocumentsData[uiState.activeDocumentSlide].highlights.map((h, idx) => (
                    <div key={idx} className="highlight-card">
                      <div className="highlight-icon"><Award size={24} /></div>
                      <p className="highlight-text">{h}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="marrakech-sidebar">
              <div className="marrakech-document-compact">
                <div className="document-compact-header">
                  <div className="document-compact-icon"><Files size={32} /></div>
                  <h4>{t.marrakech.officialDocument}</h4>
                </div>
                <div className="document-compact-info">
                  <div className="compact-info-item"><Files size={16} /><span>{marrakechDocumentsData[uiState.activeDocumentSlide].documentInfo.pages}</span></div>
                  <div className="compact-info-item"><Globe size={16} /><span>{marrakechDocumentsData[uiState.activeDocumentSlide].documentInfo.language}</span></div>
                  <div className="compact-info-item"><Eye size={16} /><span>{marrakechDocumentsData[uiState.activeDocumentSlide].documentInfo.fileSize}</span></div>
                </div>
                {(() => {
                  const docInfo = marrakechDocumentsData[uiState.activeDocumentSlide].documentInfo;
                  const docUrl = docInfo.downloadUrl;
                  const isExternal = /^https?:\/\//i.test(docUrl);
                  return isExternal ? (
                    <a href={docUrl} target="_blank" rel="noopener noreferrer" className="download-btn-compact" style={{ textDecoration: 'none' }}>
                      <ArrowRight size={18} /><span>{t.marrakech.viewDocument}</span>
                    </a>
                  ) : (
                    <a href={docUrl} download={`${docInfo.title}.pdf`} className="download-btn-compact" style={{ textDecoration: 'none' }}>
                      <ArrowRight size={18} /><span>{t.marrakech.downloadPDF}</span>
                    </a>
                  );
                })()}
              </div>

              <div className="marrakech-navigation-compact">
                <button className="nav-compact-btn" onClick={() => updateUiState({ activeDocumentSlide: (uiState.activeDocumentSlide - 1 + marrakechDocumentsData.length) % marrakechDocumentsData.length })} aria-label={t.marrakech.prevDoc}>
                  <ChevronDown style={{ transform: 'rotate(90deg)' }} size={20} />
                </button>
                <div className="nav-compact-dots">
                  {marrakechDocumentsData.map((_, index) => (
                    <button key={index} onClick={() => updateUiState({ activeDocumentSlide: index })} className={`nav-dot ${index === uiState.activeDocumentSlide ? 'active' : ''}`} aria-label={`Document ${index + 1}`} />
                  ))}
                </div>
                <button className="nav-compact-btn" onClick={() => updateUiState({ activeDocumentSlide: (uiState.activeDocumentSlide + 1) % marrakechDocumentsData.length })} aria-label={t.marrakech.nextDoc}>
                  <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="marrakech-progress">
          <div className="marrakech-progress-bar" style={{ width: `${((uiState.activeDocumentSlide + 1) / marrakechDocumentsData.length) * 100}%` }}></div>
        </div>
      </section>

      {/* ============================================================
          NEWS & EVENTS
      ============================================================ */}
      <section id="actualites" className="section section-gray">
        <div className="container">
          <div className="section-title">
            <h2>{t.news.sectionTitle}</h2>
            <div className="divider"></div>
          </div>
          <div className="grid grid-3">
            {articles.length > 0 ? (
              articles.map((article) => (
                <article key={article.id} className="news-card">
                  <div className="news-image">
                    <img src={article.imageArticle || article.image || '/91.jpg'} alt={article.title} loading="lazy" decoding="async" />
                    <div className="news-date">
                      {new Date(article.publish_date).toLocaleDateString(uiState.currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                  <div className="news-content">
                    <h3 className="news-title">{article.title}</h3>
                    <p className="news-excerpt">{article.excerpt}</p>
                    <Link to={`/news/${article.id}`} className="news-link">
                      {t.news.readFullArticle}<ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <>
                {[{ img: '/91.jpg', date: 'June 27, 2024', title: '2nd African International Conference on NPMs', excerpt: 'Cape Town, South Africa — The Second African International Conference on National Preventive Mechanisms against Torture (NPMs)...' },
                  { img: '/9.jpg',  date: 'December 7, 2024', title: 'Rabat Hosts Permanent Secretariat', excerpt: "Ms. Amina Bouayach, Chairperson of the African National Preventive Mechanisms Network (ANPMN)..." },
                  { img: '/988.jpg', date: 'June 26, 2025',  title: '3rd Conference in Praia', excerpt: 'The African National Preventive Mechanisms Network concluded its General Assembly today in Praia...' }
                ].map((n, i) => (
                  <article key={i} className="news-card">
                    <div className="news-image"><img src={n.img} alt={n.title} loading="lazy" decoding="async" /><div className="news-date">{n.date}</div></div>
                    <div className="news-content">
                      <h3 className="news-title">{n.title}</h3>
                      <p className="news-excerpt">{n.excerpt}</p>
                      <Link to="#" className="news-link">{t.news.readFullArticle}<ArrowRight size={16} /></Link>
                    </div>
                  </article>
                ))}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============================================================
          SECRÉTARIAT PERMANENT
      ============================================================ */}
      <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
        <section id="secretariat-section" className="secretariat-revolutionary" style={{ backgroundImage: `url(17.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
          <div className="secretariat-slide-overlay"></div>
          <div className="container secretariat-slide-content">
            <div className="secretariat-content-grid">
              <div className="secretariat-main-content">
                <div className="secretariat-header">
                  <span className="secretariat-subtitle">{t.secretariat.subtitle}</span>
                  <h2 className="secretariat-title">{t.secretariat.sectionTitle}</h2>
                  <div className="secretariat-title-underline"></div>
                  <p className="secretariat-description">{t.secretariat.description}</p>
                </div>
                <div className="secretariat-features-modern">
                  {[{ icon: Globe, ...t.secretariat.features[0] }, { icon: Award, ...t.secretariat.features[1] }, { icon: Users, ...t.secretariat.features[2] }, { icon: Briefcase, ...t.secretariat.features[3] }].map((f, i) => (
                    <div key={i} className="feature-modern">
                      <div className="feature-modern-icon"><f.icon size={28} /></div>
                      <div className="feature-modern-content"><h2>{f.title}</h2><p>{f.description}</p></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="secretariat-image-modern">
                <div className="secretariat-info-wrapper">
                  <div className="secretariat-contact-modern">
                    <div className="contact-modern-item">
                      <MapPin className="contact-modern-icon" size={24} />
                      <div className="contact-modern-text">
                        <h4>{t.secretariat.contact.addressTitle}</h4>
                        <p>{t.secretariat.contact.addressLine1}</p>
                        <p>{t.secretariat.contact.addressLine2}</p>
                      </div>
                    </div>
                    <div className="contact-modern-item">
                      <Phone className="contact-modern-icon" size={24} />
                      <div className="contact-modern-text"><h4>{t.secretariat.contact.phoneTitle}</h4><p>{t.secretariat.contact.phone}</p></div>
                    </div>
                    <div className="contact-modern-item">
                      <Mail className="contact-modern-icon" size={24} />
                      <div className="contact-modern-text"><h4>{t.secretariat.contact.emailTitle}</h4><p>{t.secretariat.contact.email}</p></div>
                    </div>
                    <div className="contact-modern-item">
                      <Calendar className="contact-modern-icon" size={24} />
                      <div className="contact-modern-text"><h4>{t.secretariat.contact.hoursTitle}</h4><p>{t.secretariat.contact.hours}</p></div>
                    </div>
                  </div>
                </div>
                <div className="floating-particles">
                  {[...Array(6)].map((_, i) => (<div key={i} className={`particle particle-${i + 1}`} style={{ animationDelay: `${i * 0.5}s` }}></div>))}
                </div>
              </div>
            </div>
          </div>
          <div className="secretariat-progress"><div className="secretariat-progress-bar" style={{ width: '100%' }}></div></div>
        </section>
      </Suspense>

      {/* ============================================================
          FOOTER
      ============================================================ */}
      <footer className="footer">
        <div className="footer-background-overlay"></div>
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <div className="footer-logo-icon"><Globe size={20} /></div>
                <div>
                  <h3>ANPMN</h3>
                  <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>{t.footer.tagline}</p>
                </div>
              </div>
              <p className="footer-description">{t.footer.description}</p>
            </div>

            <div className="footer-section">
              <h4>{t.footer.quickLinks}</h4>
              <ul>
                <li><a href="#apropos">{t.footer.aboutUs}</a></li>
                <li><a href="#equipe">{t.footer.members}</a></li>
                <li><a href="#actualites">{t.footer.newsEvents}</a></li>
                <li><a href="#secretariat-section">{t.footer.secretariat}</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>{t.footer.ourMissions}</h4>
              <ul>
                <li><a href="#">{t.footer.promote}</a></li>
                <li><a href="#">{t.footer.support}</a></li>
                <li><a href="#">{t.footer.foster}</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>{t.footer.contact}</h4>
              <div className="contact-item"><MapPin className="contact-icon" size={20} /><span>{t.footer.address}</span></div>
              <div className="contact-item"><Phone className="contact-icon" size={20} /><span>{t.secretariat.contact.phone}</span></div>
              <div className="contact-item"><Mail className="contact-icon" size={20} /><span>{t.secretariat.contact.email}</span></div>
              <div className="social-links">
                <a href="https://www.facebook.com/cndhmaroc.ar" className="social-link" aria-label="Facebook"><Facebook size={20} /></a>
                <a href="https://x.com/cndhmaroc"               className="social-link" aria-label="Twitter"><Twitter size={20} /></a>
                <a href="https://youtube.com/cndhmaroc"          className="social-link" aria-label="YouTube"><Youtube size={20} /></a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              {t.footer.copyright}
              <a href="#">{t.footer.legalNotice}</a>
              <a href="#">{t.footer.privacy}</a>
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default App;
